import { useState } from 'react';
import { showModal } from '@actions/common/actionCreators';
import { sendOtp, verifyOTP } from '@actions/broker/selfDesignationOtpActionCreators';
import { configs } from '@constants/configs/configs';
import { commonModalTypes as MODAL_TYPES } from '@constants/common/modals/commonModalTypes';
import {
    ChannelType,
    StatusType,
    SelfDesignationOtpSendResponse,
    ResendConfigType,
} from '../types/selfDesignationOtpTypes';
import config from '../../../../config/app.json';
import useTimer from '@hooks/useTimer';

export const EXPIRE_OTP_TIMER = config.appConfig.broker.selfDesignationOtp.timerInMinutes;
export const OTP_LENGTH = config.appConfig.broker.selfDesignationOtp.otpLength;

export default ({ dispatch, hideModal, becomeAOR, edeSearchResult, navigate, role }) => {
    const [otp, setOtp] = useState(Array<string>(OTP_LENGTH).fill(''));
    const [otpError, setOtpError] = useState('');

    const [isLoading, setIsLoading] = useState(true);
    const [otpState, setOtpState] = useState({
        otpMedium: ChannelType.SMS,
        otpMediumValue: '',
        remainingSendCount: 0,
        remainingVerifyCount: 0,
        resendTimerValue: 0,
        expirationTimerValue: EXPIRE_OTP_TIMER,
        resendCount: 0,
        resendMedium: '',
    });
    const [resendConfig, setResetConfig] = useState<ResendConfigType[]>([]);

    const {
        rawTime: resendTimerRawTime,
        start: resendTimerStart,
        reset: resendTimerReset,
        stop: resendTimerStop,
        isRunning: resendTimerIsRunning,
    } = useTimer(otpState.resendTimerValue);
    const {
        rawTime: expirationTimerRawTime,
        start: expirationTimerStart,
        reset: expirationTimerReset,
        stop: expirationTimerStop,
        isRunning: expirationTimerIsRunning,
    } = useTimer(otpState.expirationTimerValue);

    const sendOTPCall = async () => {
        setOtpError('');
        const payload = {
            encryptedHouseholdId: edeSearchResult?.primaryHousehold?.householdId,
            identityProofingId: edeSearchResult?.identityProofId,
        };
        const sendOtpResult = await dispatch(sendOtp({ payload }));
        setIsLoading(false);
        const { status } = sendOtpResult;

        if (status === StatusType.SUCCESS) {
            const { otpResendCooldown, target, channel, attemptsLeft } = processSendResult(sendOtpResult);
            setOtpState((prevState) => ({
                ...prevState,
                otpMedium: channel,
                otpMediumValue: target,
                remainingSendCount: attemptsLeft,
                resendTimerValue: otpResendCooldown,
            }));

            resendTimerStart();
            expirationTimerStart();
        } else {
            hideModal();
            resendTimerStop();
            expirationTimerStop();
            showErrorModal(status);
        }
    };

    const resendOTPCall = async (key: string) => {
        setOtpError('');
        setOtp(Array<string>(OTP_LENGTH).fill(''));
        setOtpState((prevState) => ({
            ...prevState,
            resendMedium: key,
        }));
        const payload = {
            encryptedHouseholdId: edeSearchResult?.primaryHousehold?.householdId,
            identityProofingId: edeSearchResult?.identityProofId,
            channel: key,
        };
        const sendOtpResult = await dispatch(sendOtp({ payload }));
        const { status, otpResendCooldown, attemptsLeft } = processSendResult(sendOtpResult);
        if (status === StatusType.SUCCESS) {
            setOtpState((prevState) => ({
                ...prevState,
                resendTimerValue: otpResendCooldown,
                remainingSendCount: attemptsLeft,
                resendCount: prevState.resendCount + 1,
            }));

            resendTimerReset();
            resendTimerStart();
        } else {
            hideModal();
            resendTimerStop();
            expirationTimerStop();
            showErrorModal(status);
        }
    };

    const onSubmit = async () => {
        const otpValue = otp.join('');
        const payload = {
            encryptedHouseholdId: edeSearchResult.primaryHousehold.householdId,
            otp: otpValue,
            ...(otpState.resendMedium && { channel: otpState.resendMedium }),
        };
        const verificationResult = await dispatch(verifyOTP({ payload }));
        const { status, attemptsLeft } = verificationResult;
        if (status === StatusType.SUCCESS) {
            setOtpError('');
            hideModal();
            becomeAOR();
        } else if (status === StatusType.FAILURE) {
            if (attemptsLeft === 0) {
                showErrorModal(StatusType.MAX_ATTEMPT);
                hideModal();
            } else {
                setOtpError('invalid');
                setOtpState((prevState) => ({
                    ...prevState,
                    remainingVerifyCount: attemptsLeft,
                }));
            }
        } else {
            hideModal();
            resendTimerStop();
            expirationTimerStop();
            showErrorModal(status);
        }
    };

    const processSendResult = (response: SelfDesignationOtpSendResponse) => {
        const resendChannel = response.resendChannel || {};

        const updatedResendOptions: ResendConfigType[] = Object.entries({
            [ChannelType.EMAIL]: resendChannel.hasEmail,
            [ChannelType.SMS]: resendChannel.resendSms,
            [ChannelType.CALL]: resendChannel.resendCall,
        }).map(([key, visible]) => ({
            label: `selfDesignation.${role}.search.searchResult.modal.otp.resend.button.${key.toLowerCase()}`,
            visible: !!visible,
            key: key as ChannelType,
        }));

        setResetConfig(updatedResendOptions);

        const {
            status,
            otpRequestTime,
            otpResendCooldown = 0,
            attemptsLeft,
            data: { target = '', channel = ChannelType.SMS } = {},
        } = response;

        return {
            status,
            otpRequestTime,
            otpResendCooldown,
            attemptsLeft,
            target,
            channel,
        };
    };

    const showErrorModal = (status: StatusType) => {
        dispatch(
            showModal({
                modalType: MODAL_TYPES.SELF_DESIGNATION_OTP_ERROR_MODAL,
                modalData: {
                    selfDesignationOtpErrorType: status,
                },
            }),
        );
    };

    const onCloseModal = () => {
        hideModal();
        resendTimerStop();
        expirationTimerStop();
        navigate(configs.routes.EDE_ID_PROOFING);
    };

    return {
        otp,
        setOtp,
        otpError,
        setOtpError,
        isLoading,
        setIsLoading,
        resendTimerRawTime,
        resendTimerIsRunning,
        expirationTimerRawTime,
        expirationTimerStart,
        resendCount: otpState.resendCount,
        remainingSendCount: otpState.remainingSendCount,
        remainingVerifyCount: otpState.remainingVerifyCount,
        otpMedium: otpState.otpMedium,
        otpMediumValue: otpState.otpMediumValue,
        sendOTPCall,
        resendOTPCall,
        onSubmit,
        showErrorModal,
        onCloseModal,
        resendConfig,
    };
};
